﻿import { useState, useCallback, useEffect } from 'react';
import './styles.css';
import BrandLogo from './components/BrandLogo';
import scoringDark from 'url:./images/screens/scoring-dark.png';
import scoringLight from 'url:./images/screens/scoring-light.png';
import eventsDark from 'url:./images/screens/events-dark.png';
import eventsLight from 'url:./images/screens/events-light.png';
import recapDark from 'url:./images/screens/recap-dark.png';
import recapLight from 'url:./images/screens/recap-light.png';
import settlementDark from 'url:./images/screens/settlement-dark.png';
import settlementLight from 'url:./images/screens/settlement-light.png';
// import draftDark from 'url:./videos/draft-dark.mp4';
// import draftLight from 'url:./videos/draft-light.mp4';
// import pairingsDark from 'url:./videos/pairings-dark.mp4';
// import pairingsLight from 'url:./videos/pairings-light.mp4';
// import leaderboardDark from 'url:./videos/leaderboard-dark.mp4';
// import leaderboardLight from 'url:./videos/leaderboard-light.mp4';

const APP_URL = 'https://app.sideloop.golf';

// ─────────────────────────────────────────────────────────────────
// ANNUAL PRICING CONFIG
// Update these values when you have final annual prices.
//   blueMonthlyDisplay  — the per-month equivalent shown on the card
//   champMonthlyDisplay — the per-month equivalent shown on the card
//   voiceMonthlyDisplay — the per-month equivalent shown on the Voice add-on card
//   blueTotalNote       — "Billed $XX.XX / year" note under price
//   champTotalNote      — "Billed $XX.XX / year" note under price
//   voiceTotalNote      — "Billed $XX.XX / year" note under Voice add-on price
//   blueSave            — savings badge on Blue Tees card
//   champSave           — savings badge on Championship Tees card
//   voiceSave           — savings badge on Voice Scoring card
//   topPill             — pill shown next to the Annual toggle button
// ─────────────────────────────────────────────────────────────────
const ANNUAL = {
  blueMonthlyDisplay:  '$5.00',
  champMonthlyDisplay: '$8.33',
  voiceMonthlyDisplay: '$4.16',
  blueTotalNote:       'Billed $59.99 / year',
  champTotalNote:      'Billed $99.99 / year',
  voiceTotalNote:      'Billed $49.99 / year',
  blueSave:            'Save 17%',
  champSave:           'Save 17%',
  voiceSave:           'Save 17%',
  topPill:             'Save 17%',
};

// ─────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────
function useDeviceCycle(intervalMs = 5000) {
  const [deviceType, setDeviceType] = useState('iphone');
  useEffect(() => {
    const timer = setInterval(() => {
      setDeviceType(prev => prev === 'iphone' ? 'android' : 'iphone');
    }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return deviceType;
}

function useScreenCycle(intervalMs = 5000) {
  const [screenType, setScreenType] = useState('tv');
  useEffect(() => {
    const timer = setInterval(() => {
      setScreenType(prev => prev === 'tv' ? 'macbook' : 'tv');
    }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return screenType;
}

function useCurrentMode() {
  const [mode, setMode] = useState(() => document.documentElement.getAttribute('data-mode') || 'dark');
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setMode(document.documentElement.getAttribute('data-mode') || 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode'] });
    return () => observer.disconnect();
  }, []);
  return mode;
}

// ─────────────────────────────────────────────────────────────────
// ASSET CONFIG — uncomment imports at top of file as you add assets
// ─────────────────────────────────────────────────────────────────
const PHONE_ASSETS = {
  scoring: { dark: scoringDark, light: scoringLight },
  events:  { dark: eventsDark, light: eventsLight },
  recap:   { dark: recapDark, light: recapLight },
  settlement: { dark: settlementDark, light: settlementLight },
};

const VIDEO_ASSETS = {
  draft:       { dark: null, light: null },
  pairings:    { dark: null, light: null },
  leaderboard: { dark: null, light: null },
};

// ─────────────────────────────────────────────────────────────────
// MARQUEE ITEMS
// ─────────────────────────────────────────────────────────────────
const MARQUEE_ITEMS = [
  'Nassau', 'Skins', 'Wolf', 'Match Play', 'Fives', 'Tucson Twist',
  'Umbriago', 'Stableford', 'Snake', 'Defender', 'Hammer',
  'Vegas', 'Bingo Bango Bongo', 'Round Robin', 'Rabbit',
  'Team Events', 'Round Recaps', 'Bet Settlement',
];

// ─────────────────────────────────────────────────────────────────
// MOCK ROWS (Scoring / Events / Recaps)
// ─────────────────────────────────────────────────────────────────
const MOCK_ROWS = [
  {
    flip: false,
    assetKey: 'scoring',
    screenlabel: 'App Screenshot — Scoring',
    label: 'Scoring',
    title: <>Simple scoring that<br />handles <em>everything.</em></>,
    body: 'Take the human element out of scoring, let Side Loop do the heavy lifting and simply enjoy the round with your friends.',
    bullets: [
      'Auto-defaults to par — just tap up or down',
      'Net stroke dots for every active game',
      'Live match play and Nassau standings per hole',
      'Multi-player sync for event group scoring',
      'Take complex scoring out of your hands',
    ],
  },
  {
    flip: true,
    assetKey: 'events',
    screenlabel: 'App Screenshot — Event',
    label: 'Events',
    title: <>From a foursome<br />to a <em>full event.</em></>,
    body: 'Create team events with 8 to 200+ players. Draft teams, set up multi-round scoring, assign groups, and let every group run their own side bets. Live leaderboards track points across rounds.',
    bullets: [
      'Snake or straight team draft',
      'Auto set group pairings by handicap',
      'Event-level + group-level side games',
      'Live scoring sync across all groups',
      'Online event leaderboard with public access',
    ],
  },
  {
    flip: false,
    assetKey: 'settlement',
    screenlabel: 'App Screenshot — Settlement',
    label: 'Bet Settlement',
    title: <>Stack your games.<br />Settle in <em>seconds.</em></>,
    body: 'Run as many side games as you want in a single round. Side Loop tracks every bet across every game and settles them all at once — optimized for the fewest transactions between players.',
    bullets: [
      'Nassau, Skins, Match Play, Wolf, Fives — all in one round',
      'Each game calculates independently with full accuracy',
      'Optimized payout grid — fewest transactions possible',
      'Net amounts per player across every active game',
      'No more napkin math at the 19th hole',
    ],
  },
  {
    flip: true,
    assetKey: 'recap',
    screenlabel: 'App Screenshot — Recap',
    label: 'Recaps',
    title: <>Multiple voices.<br />One round.<br /><em>Infinite laughs.</em></>,
    body: 'Every score, streak, and blown lead — analyzed and turned into a personalized recap in the voice of your choosing. Share it and watch the replies roll in.',
    bullets: [
      'Roast Mode — savage, no mercy',
      'Famous movie characters and real-life celebrities',
      'Sunday at the Majors — Professional broadcaster reverence',
      'Auto-detects streaks, collapses, and comebacks',
      'Laugh at yourself and your friends',
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
// WATCH LIVE ITEMS
// ─────────────────────────────────────────────────────────────────
const WATCH_LIVE_ITEMS = [
  {
    flip: false,
    assetKey: 'draft',
    label: 'Live Draft',
    title: <>Watch captains build<br />their teams <em>live.</em></>,
    body: 'Share the draft board link with friends, family, and players. Every pick, every steal, every Mr. Irrelevant — happening in real time on any screen.',
  },
  {
    flip: true,
    assetKey: 'pairings',
    label: 'Live Pairings',
    title: <>Every round.<br />Every matchup.<br /><em>Instantly.</em></>,
    body: "Pairings update live as captains set each round. Players and spectators always know exactly who's playing who — on their phone, tablet, or the clubhouse TV.",
  },
  {
    flip: false,
    assetKey: 'leaderboard',
    label: 'Live Scoring',
    title: <>Hole by hole.<br />Every moment<br /><em>captured.</em></>,
    body: 'The leaderboard updates with every score entered from every group. Friends and family follow along from anywhere — no app needed, just a link.',
  },
];

// ─────────────────────────────────────────────────────────────────
// FEATURES
// ─────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    num: '01',
    title: 'Simple Scoring',
    desc: 'Tap through each hole with auto-par defaults. Stroke indicators and net calculations update in real time for every game in play.',
  },
  {
    num: '02',
    title: '20 Betting Formats',
    desc: 'Nassau, Skins, Wolf, Match Play, Fives, Tucson Twist, Umbriago, Vegas, Stableford, and 11 more — with presses, carryovers, birdie doubles, and automatic settlement.',
  },
  {
    num: '03',
    title: 'Round Recaps in 20+ Voices',
    desc: 'Choose from over 20 recap styles — from savage roasts to Shakespearean verse. Every streak, collapse, and comeback analyzed.',
  },
  {
    num: '04',
    title: 'Team Events',
    desc: 'Run Ryder Cup-style events, large events, or your regular multi-group game. Draft teams, assign groups, configure multi-round scoring with live leaderboards.',
  },
  {
    num: '05',
    title: 'Group Betting',
    desc: 'Each group in your event runs their own side games — Nassau, Skins, Match Play — independent from event scoring. Everyone gets their action.',
  },
  {
    num: '06',
    title: 'Instant Settlement',
    desc: 'See exactly who owes who at the end of every round. No more napkin math at the 19th hole. Including the optimal payouts for the least transactions.',
  },
  {
    num: '07',
    title: 'Voice Scoring',
    desc: 'Score your round, configure games, and track shot details — all by voice. Say "Jason got a birdie" or "set up a $5 Nassau" and the AI handles the rest.',
    badge: 'Add-On',
  },
];

// ─────────────────────────────────────────────────────────────────
// RECAP SAMPLES
// ─────────────────────────────────────────────────────────────────
const RECAPS = [
  {
    style: '🔥 Roast Mode',
    quote: '"Four consecutive bogeys starting on the easiest hole on the course. That\'s not a slump, that\'s a lifestyle choice. Someone check if Dave\'s clubs are from a yard sale..."',
  },
  {
    style: '🎭 Shakespeare',
    quote: '"Hark! Upon the seventh fairway, fortune\'s wheel did turn most cruel. Three strokes above the mark, a tragedy most foul, as bogey\'s shadow fell upon the green..."',
  },
  {
    style: '⛳ Sunday at the Majors',
    quote: '"And as the afternoon shadows stretched across the 18th green, what began as a quiet Sunday round had become something far more memorable..."',
  },
];

// ─────────────────────────────────────────────────────────────────
// EVENT FEATURES
// ─────────────────────────────────────────────────────────────────
const EVENT_FEATURES = [
  { title: 'Team Draft',       desc: 'Snake or straight draft with captain picks. Or assign teams manually.' },
  { title: 'Group Pairings',   desc: 'Auto-pair by handicap or manually assign matchups for each round.' },
  { title: 'Live Scoring',     desc: 'Every group scores simultaneously with real-time sync across devices.' },
  { title: 'Leaderboard',      desc: 'Publicly accessible per-round and overall standings with team points and individual stats.' },
];

// ─────────────────────────────────────────────────────────────────
// NAV
// ─────────────────────────────────────────────────────────────────
function Nav() {
  return (
    <nav className="site-nav">
      <BrandLogo showTagline={false} />
      <div className="nav-right">
        <div className="nav-links">
          <a href="#watch-live">Watch Live</a>
          <a href="#features">Features</a>
          <a href="#events">Events</a>
          <a href="#recaps">Recaps</a>
          <a href="#pricing">Pricing</a>
        </div>
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="hero">
      <div className="hero-glow" />
      <p className="hero-eyebrow fade-up delay-1">The home of the side action</p>
      <h1 className="hero-h1 fade-up delay-2">
        Every round<br />
        has a<br />
        <em>story.</em>
      </h1>
      <p className="hero-subtitle fade-up delay-3">
        Score it simply. Bet on it seriously. Settle it quickly. Run events your way.
        Then let Side Loop write the recap that tells your story.
      </p>
      <div className="hero-buttons fade-up delay-4">
        <a href="#features" className="btn-secondary btn-secondary--featured">See How It Works</a>
      </div>
      <div className="scroll-indicator fade-up delay-5">
        <div className="scroll-line" />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// MARQUEE
// ─────────────────────────────────────────────────────────────────
function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS]; // duplicate for seamless loop
  return (
    <div className="marquee-wrap">
      <div className="marquee-track">
        {items.map((text, i) => (
          <span key={i} className="marquee-item">
            <span className="marquee-text">{text}</span>
            <span className="marquee-dot">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MOCK SCREENS
// ─────────────────────────────────────────────────────────────────
function MockScreens() {
  const deviceType = useDeviceCycle(5000);
  const mode = useCurrentMode();

  return (
    <div className="mock-section">
      {MOCK_ROWS.map((row, i) => {
        const assetSrc = PHONE_ASSETS[row.assetKey]?.[mode];
        const screen = (
          <div className={`device-frame device-frame--${deviceType}`}>
            <div className="device-frame__notch" />
            <div className="device-frame__screen">
              {assetSrc ? (
                <img src={assetSrc} alt={row.screenlabel} className="device-frame__img" />
              ) : (
                <div className="device-frame__placeholder">
                  <span>{row.screenlabel}</span>
                </div>
              )}
            </div>
          </div>
        );
        const copy = (
          <div className="mock-content">
            <p className="section-label">{row.label}</p>
            <h3 className="mock-title">{row.title}</h3>
            <p className="mock-body">{row.body}</p>
            <ul className="mock-bullets">
              {row.bullets.map((b, j) => (
                <li key={j}><span className="bullet-check">✓</span> {b}</li>
              ))}
            </ul>
          </div>
        );
        return (
          <div key={i} className={`mock-row${row.flip ? ' flipped' : ''}`}>
            {row.flip ? <>{copy}{screen}</> : <>{screen}{copy}</>}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FEATURES SECTION
// ─────────────────────────────────────────────────────────────────
function Features() {
  return (
    <section id="features" className="section">
      <div className="section-header">
        <p className="section-label">What You Get</p>
        <h2 className="section-title">Everything your<br />golf group needs.</h2>
        <p className="section-desc">
          Simple scoring, every betting format, team events, and personalized recaps
          — from a casual foursome to a large event.
        </p>
      </div>
      <div className="features-grid">
        {FEATURES.map(f => (
          <div key={f.num} className="feature-card">
            <div className="feature-num">{f.num}</div>
            {f.badge ? (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                <div className="feature-title">{f.title}</div>
                <span style={{
                  display: 'inline-block',
                  backgroundColor: 'rgba(22, 163, 74, 0.15)',
                  color: '#16a34a',
                  fontWeight: 600,
                  fontSize: '11px',
                  borderRadius: '6px',
                  padding: '2px 8px',
                }}>{f.badge}</span>
              </div>
            ) : (
              <div className="feature-title">{f.title}</div>
            )}
            <div className="feature-desc">{f.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// RECAPS SECTION
// ─────────────────────────────────────────────────────────────────
function Recaps() {
  return (
    <section id="recaps" className="section">
      <div className="section-header">
        <p className="section-label">Sample Recaps</p>
        <h2 className="section-title">A taste of the drama.</h2>
        <p className="section-desc">
          Every recap is unique — built from your actual scores, streaks, and betting outcomes.
        </p>
      </div>
      <div className="recap-grid">
        {RECAPS.map(r => (
          <div key={r.style} className="recap-card">
            <div className="recap-style">{r.style}</div>
            <div className="recap-quote">{r.quote}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// WATCH LIVE SECTION
// ─────────────────────────────────────────────────────────────────
function WatchLive() {
  const screenType = useScreenCycle(5000);
  const mode = useCurrentMode();
  const [expandedVideo, setExpandedVideo] = useState(null);

  return (
    <section id="watch-live" className="section">
      <div className="section-header">
        <p className="section-label">Watch Live</p>
        <h2 className="section-title">Share the action<br />with everyone.</h2>
        <p className="section-desc">
          Public pages anyone can access — no app, no login. Put the leaderboard on the clubhouse TV
          or send the link to friends and family following from home.
        </p>
      </div>

      <div className="watch-live-rows">
        {WATCH_LIVE_ITEMS.map((item, i) => {
          const videoSrc = VIDEO_ASSETS[item.assetKey]?.[mode];
          const screen = (
            <div
              className={`screen-frame screen-frame--${screenType}`}
              onClick={() => setExpandedVideo({ src: videoSrc, label: item.label })}
            >
              <div className="screen-frame__bezel">
                {screenType === 'tv' && <div className="screen-frame__speaker screen-frame__speaker--left"><div /><div /><div /><div /><div /><div /><div /><div /></div>}
                <div className="screen-frame__viewport">
                  {videoSrc ? (
                    <video
                      key={`${item.assetKey}-${mode}`}
                      src={videoSrc}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="screen-frame__video"
                    />
                  ) : (
                    <div className="screen-frame__placeholder">
                      <span>{item.label} — {screenType === 'tv' ? 'TV' : 'MacBook'} view</span>
                    </div>
                  )}
                </div>
                {screenType === 'tv' && <div className="screen-frame__speaker screen-frame__speaker--right"><div /><div /><div /><div /><div /><div /><div /><div /></div>}
              </div>
              <div className="screen-frame__play-hint" />
              {screenType === 'macbook' && <div className="screen-frame__base" />}
            </div>
          );
          const copy = (
            <div className="mock-content">
              <p className="section-label">{item.label}</p>
              <h3 className="mock-title">{item.title}</h3>
              <p className="mock-body">{item.body}</p>
            </div>
          );
          return (
            <div key={i} className={`mock-row${item.flip ? ' flipped' : ''}`}>
              {item.flip ? <>{copy}{screen}</> : <>{screen}{copy}</>}
            </div>
          );
        })}
      </div>

      {expandedVideo && (
        <div className="video-modal-overlay" onClick={() => setExpandedVideo(null)}>
          <div className="video-modal-content" onClick={e => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setExpandedVideo(null)}>×</button>
            {expandedVideo.src ? (
              <video
                src={expandedVideo.src}
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            ) : (
              <div className="video-modal-placeholder">
                <span>{expandedVideo.label} — video coming soon</span>
              </div>
            )}
            <div className="video-modal-label">{expandedVideo.label}</div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// EVENTS SECTION
// ─────────────────────────────────────────────────────────────────
function Events() {
  return (
    <section id="events" className="section">
      <div className="section-header">
        <p className="section-label">Team Events</p>
        <h2 className="section-title">Your own Ryder Cup.</h2>
        <p className="section-desc">
          Organize multi-round team events with 8–200+ players.
          Draft teams, assign groups, and track points across rounds.
        </p>
      </div>
      <div className="events-grid">
        {EVENT_FEATURES.map(e => (
          <div key={e.title} className="event-card">
            <h4>{e.title}</h4>
            <p>{e.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// PRICING SECTION
// ─────────────────────────────────────────────────────────────────
function Pricing() {
  const [billing, setBilling] = useState('monthly');
  const isAnnual = billing === 'annual';

  return (
    <section id="pricing" className="section">
      <div className="section-header">
        <p className="section-label">Pricing</p>
        <h2 className="section-title">Pick your tee box.</h2>
        <p className="section-desc">Start free. Upgrade when you're hooked. In-app upgrades for your own golf group.</p>
      </div>

      {/* Billing toggle */}
      <div className="pricing-toggle-wrap">
        <div className="billing-toggle">
          <button
            className={`billing-btn${billing === 'monthly' ? ' active' : ''}`}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            className={`billing-btn${billing === 'annual' ? ' active' : ''}`}
            onClick={() => setBilling('annual')}
          >
            Annual
          </button>
        </div>
        {isAnnual && <span className="annual-pill">{ANNUAL.topPill}</span>}
      </div>

      <div className="pricing-grid-4">

        {/* ── Red Tees (Free) ─────────────────────────── */}
        <div className="pricing-card">
          <div className="pricing-tier">Red Tees</div>
          <div className="pricing-price">
            <span className="pricing-amount">$0</span>
            <span className="pricing-period">forever</span>
          </div>
          <div className="pricing-billed-note">&nbsp;</div>
          <div className="pricing-divider" />
          <ul className="pricing-bullets">
            <li><span className="bullet-check">✓</span> 2 recaps / month</li>
            <li><span className="bullet-check">✓</span> Roast &amp; Sunday at the Majors styles only</li>
            <li><span className="bullet-check">✓</span> 5 core betting games</li>
            <li><span className="bullet-check">✓</span> Unlimited rounds</li>
          </ul>
          <div className="pricing-store-note">Available in the Side Loop app</div>
        </div>

        {/* ── Blue Tees (Pro) ─────────────────────────── */}
        <div className="pricing-card highlighted">
          <div className="pricing-badge">Most Popular</div>
          <div className="pricing-tier">Blue Tees</div>
          <div className="pricing-price">
            <span className="pricing-amount">
              {isAnnual ? ANNUAL.blueMonthlyDisplay : '$5.99'}
            </span>
            <span className="pricing-period">/ month</span>
          </div>
          <div className="pricing-billed-row">
            <span className="pricing-billed-note">
              {isAnnual ? ANNUAL.blueTotalNote : '\u00a0'}
            </span>
            {isAnnual && <span className="pricing-savings">{ANNUAL.blueSave}</span>}
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-bullets">
            <li><span className="bullet-check">✓</span> 5 recaps / month</li>
            <li><span className="bullet-check">✓</span> 6 recap styles</li>
            <li><span className="bullet-check">✓</span> Course search + Scorecard Scanning</li>
            <li><span className="bullet-check">✓</span> Round history</li>
          </ul>
          <div className="pricing-store-note">Available in the Side Loop app</div>
        </div>

        {/* ── Championship Tees (Elite) ──────────────── */}
        <div className="pricing-card">
          <div className="pricing-tier">Championship Tees</div>
          <div className="pricing-price">
            <span className="pricing-amount">
              {isAnnual ? ANNUAL.champMonthlyDisplay : '$9.99'}
            </span>
            <span className="pricing-period">/ month</span>
          </div>
          <div className="pricing-billed-row">
            <span className="pricing-billed-note">
              {isAnnual ? ANNUAL.champTotalNote : '\u00a0'}
            </span>
            {isAnnual && <span className="pricing-savings">{ANNUAL.champSave}</span>}
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-bullets">
            <li><span className="bullet-check">✓</span> 20 recaps / month</li>
            <li><span className="bullet-check">✓</span> All recap styles</li>
            <li><span className="bullet-check">✓</span> Multi-group Events</li>
            <li><span className="bullet-check">✓</span> Priority support</li>
          </ul>
          <div className="pricing-store-note">Available in the Side Loop app</div>
        </div>

        <div className="pricing-card">
          <div className="pricing-tier">Voice Scoring</div>
          <div className="pricing-price">
            <span className="pricing-amount">
              {isAnnual ? ANNUAL.voiceMonthlyDisplay : '$4.99'}
            </span>
            <span className="pricing-period">/ month</span>
          </div>
          <div className="pricing-billed-row">
            <span className="pricing-billed-note">
              {isAnnual ? ANNUAL.voiceTotalNote : ' '}
            </span>
            {isAnnual && <span className="pricing-savings">{ANNUAL.voiceSave}</span>}
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-bullets">
            <li><span className="bullet-check">✓</span> Voice scoring for every hole</li>
            <li><span className="bullet-check">✓</span> Configure games and side bets hands-free</li>
            <li><span className="bullet-check">✓</span> Add spoken color commentary to your recaps</li>
            <li><span className="bullet-check">✓</span> Works with any tier</li>
          </ul>
          <div className="pricing-store-note">Available in the Side Loop app</div>
        </div>

      </div>

      {/* ── Golf Club Section ─────────────────────────── */}
      <div className="club-section">
        <div className="section-header">
          <p className="section-label">For Golf Clubs</p>
          <h2 className="section-title">Run it for your club.</h2>
          <p className="section-desc">Club-managed calcuttas and leagues, purchased and run from the web.</p>
        </div>

        <div className="pricing-grid-2">

          {/* ── Calcutta Management (Coming Soon) ─────────── */}
          <div className="pricing-card coming-soon">
            <div className="pricing-badge pricing-badge--muted">Coming Soon</div>
            <div className="pricing-tier">Calcutta Management</div>
            <div className="pricing-price">
              <span className="pricing-amount pricing-amount--placeholder">—</span>
            </div>
            <div className="pricing-billed-row"><span className="pricing-billed-note">&nbsp;</span></div>
            <div className="pricing-divider" />
            <ul className="pricing-bullets">
              <li><span className="bullet-check">✓</span> Team auctions with live bidding</li>
              <li><span className="bullet-check">✓</span> Automatic payout calculation by finish</li>
              <li><span className="bullet-check">✓</span> Public auctioneer and leaderboard screens</li>
              <li><span className="bullet-check">✓</span> Run alongside any event format</li>
            </ul>
            <a href={APP_URL} className="pricing-cta secondary" aria-disabled="true">Notify Me</a>
          </div>

          {/* ── League Management (Coming Soon) ─────────── */}
          <div className="pricing-card coming-soon">
            <div className="pricing-badge pricing-badge--muted">Coming Soon</div>
            <div className="pricing-tier">League Management</div>
            <div className="pricing-price">
              <span className="pricing-amount pricing-amount--placeholder">—</span>
            </div>
            <div className="pricing-billed-row"><span className="pricing-billed-note">&nbsp;</span></div>
            <div className="pricing-divider" />
            <ul className="pricing-bullets">
              <li><span className="bullet-check">✓</span> Recurring weekly or monthly schedules</li>
              <li><span className="bullet-check">✓</span> Season-long standings across rounds</li>
              <li><span className="bullet-check">✓</span> Handicap tracking and automatic flights</li>
              <li><span className="bullet-check">✓</span> Player dues and prize-pool tracking</li>
            </ul>
            <a href={APP_URL} className="pricing-cta secondary" aria-disabled="true">Notify Me</a>
          </div>

        </div>
      </div>

    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// FOOTER — Landing page (4 columns)
// ─────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="landing-footer">
      <div className="landing-footer-grid">
        <div className="landing-footer-col landing-footer-brand">
          <BrandLogo />
        </div>

        <div className="landing-footer-col">
          <div className="landing-footer-heading">Events</div>
          <ul className="landing-footer-links">
            <li><a href="/dashboard.html">Dashboard</a></li>
            <li><a href="/leaderboard.html">Leaderboard</a></li>
            <li><a href="/pairings.html">Pairings</a></li>
            <li><a href="/draft.html">Draft</a></li>
            <li><a href="/admin.html">Admin</a></li>
          </ul>
        </div>

        <div className="landing-footer-col">
          <div className="landing-footer-heading">Calcutta</div>
          <ul className="landing-footer-links">
            <li><a href="/calcutta.html">Calcutta Admin</a></li>
            <li><a href="/calcutta-live.html">Live View</a></li>
            <li><a href="/calcutta-auctioneer.html">Auctioneer</a></li>
            <li><a href="/calcutta-spotter.html">Spotter</a></li>
          </ul>
        </div>

        <div className="landing-footer-col">
          <div className="landing-footer-heading">Platform</div>
          <ul className="landing-footer-links">
            <li><a href="/mysideloop.html">My Side Loop</a></li>
            <li><a href="/leagues.html">Leagues</a></li>
            <li><a href="/course.html">Course Directory</a></li>
            <li><a href="/help.html">Help Center</a></li>
          </ul>
        </div>

        <div className="landing-footer-col">
          <div className="landing-footer-heading">Support</div>
          <ul className="landing-footer-links">
            <li><a href="mailto:support@sideloop.golf">support@sideloop.golf</a></li>
            <li><a href="/report-course.html">Report course data issue</a></li>
            <li><a href="/submit-game.html">Submit a side game idea</a></li>
            <li><a href="/privacy.html">Privacy Policy</a></li>
            <li><a href="/terms.html">Terms of Service</a></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────
// THEME TOGGLE
// ─────────────────────────────────────────────────────────────────
function ThemeToggle() {
  const [theme, setTheme]   = useState('dark');
  const [open, setOpen]     = useState(false);

  const applyTheme = useCallback((mode) => {
    setTheme(mode);
    if (mode === 'light') {
      document.documentElement.setAttribute('data-mode', 'light');
    } else {
      document.documentElement.removeAttribute('data-mode');
    }
    setOpen(false);
  }, []);

  // Close panel on outside click
  const handleOutside = useCallback((e) => {
    if (!e.target.closest('.theme-toggle')) setOpen(false);
  }, []);

  useState(() => {
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  });

  return (
    <div className="theme-toggle">
      {open && (
        <div className="theme-panel">
          <div className="theme-panel-label">Appearance</div>
          <div className="theme-buttons">
            <button
              className={`theme-btn${theme === 'dark' ? ' active' : ''}`}
              onClick={() => applyTheme('dark')}
            >
              🌙 Dark
            </button>
            <button
              className={`theme-btn${theme === 'light' ? ' active' : ''}`}
              onClick={() => applyTheme('light')}
            >
              ☀️ Light
            </button>
          </div>
          <div className="theme-note">Switches all text, backgrounds, borders, and colors.</div>
        </div>
      )}
      <button className="theme-fab" onClick={() => setOpen(o => !o)}>
        {theme === 'dark' ? '🌙' : '☀️'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <>
      <div className="grain-overlay" />
      <div className="bg-gradient" />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <MockScreens />
        <WatchLive />
        <Features />
        <Events />
        <Recaps />
        <Pricing />
      </main>
      <Footer />
      <ThemeToggle />
    </>
  );
}
